import React, { useState } from "react";
import { createLogger } from "../../utilities/logger";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import { ServiceBase } from "../../utilities/services/service-base";

const log = createLogger("TestLoggingTool");

const COMPONENT_CLASS = "c-test-logging-tool";

const TestLoggingTool: React.FC = () => {
    const { state } = useAuthState();
    const [level, setLevel] = useState<"info" | "warn" | "error">("info");
    const [sent, setSent] = useState(false);
    const [apiLoading, setApiLoading] = useState(false);
    const [apiResult, setApiResult] = useState<string | null>(null);

    const sendTestLog = () => {
        const context = {
            trigger: "smoke-test",
            level: level,
            // auth uid — must equal the server-stamped jsonPayload.identity
            authenticationId: state.user?.authenticationId ?? null,
        };

        if (level === "error") {
            log.error("Smoke test log from super admin tool", {
                ...context,
                exception: new Error("Smoke test exception"),
            });
        } else if (level === "warn") {
            log.warn("Smoke test log from super admin tool", context);
        } else {
            log.info("Smoke test log from super admin tool", context);
        }

        setSent(true);
        setTimeout(() => setSent(false), 5000);
    };

    const triggerApiErrorLog = async () => {
        setApiLoading(true);
        setApiResult(null);
        try {
            await ServiceBase.list<unknown>("/admin/test-log/error")();
            setApiResult("Unexpected: the API request succeeded.");
        } catch (e: any) {
            const status = e?.response?.status ?? "?";
            const message = e?.response?.data?.message ?? e?.message ?? "Request failed";
            setApiResult(`API request failed (${status}): ${message}`);
        } finally {
            setApiLoading(false);
        }
    };

    return (
        <div className={COMPONENT_CLASS} style={{ maxWidth: 600 }}>
            <h3>Test Logging</h3>
            <p style={{ color: "#666" }}>
                Sends a test log through the canonical pipeline to Cloud Logging
                (source: "portal"). Verify in Logs Explorer:
            </p>
            <code
                style={{
                    display: "block",
                    background: "#f3f4f6",
                    padding: 8,
                    fontSize: 12,
                }}
            >
                jsonPayload.data.source="portal" AND
                jsonPayload.data.trigger="smoke-test"
            </code>

            <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
                <select
                    value={level}
                    onChange={(e) =>
                        setLevel(e.target.value as "info" | "warn" | "error")
                    }
                    style={{ padding: 8 }}
                >
                    <option value="info">info</option>
                    <option value="warn">warn</option>
                    <option value="error">error</option>
                </select>
                <button onClick={sendTestLog} style={{ padding: "8px 16px" }}>
                    Send Test Log
                </button>
            </div>

            {sent && (
                <div style={{ color: "#15803d", marginTop: 12 }}>
                    Log sent — flushes within ~5s. Query with the filter above.
                </div>
            )}

            <div style={{ marginTop: 24, borderTop: "1px solid #e5e7eb", paddingTop: 16 }}>
                <h4>API Error Log</h4>
                <p style={{ color: "#666" }}>
                    Calls the fms-api <code>/admin/test-log/error</code> endpoint, which fails on
                    purpose so the API itself writes error-severity entries to Cloud Logging
                    (source: "api"). Verify in Logs Explorer:
                </p>
                <code
                    style={{
                        display: "block",
                        background: "#f3f4f6",
                        padding: 8,
                        fontSize: 12,
                    }}
                >
                    severity=ERROR AND (jsonPayload.logger="TestLogController" OR
                    jsonPayload.logger="RequestLogger" OR
                    jsonPayload.logger="GlobalExceptionFilter") AND
                    jsonPayload.exception.message=~"Smoke test error"
                </code>

                <button
                    onClick={triggerApiErrorLog}
                    disabled={apiLoading}
                    style={{ padding: "8px 16px", marginTop: 12 }}
                >
                    {apiLoading ? "Triggering..." : "Trigger API Error Log"}
                </button>

                {apiResult && (
                    <div style={{ color: "#b91c1c", marginTop: 12 }}>{apiResult}</div>
                )}
            </div>
        </div>
    );
};

export default TestLoggingTool;